import { useState, useMemo, useEffect } from "react";
import {
  Maximize2,
  Settings,
  X,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Package,
  Tag,
  Boxes,
  Layers,
  Info,
  Loader2,
  Hash
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useNavigation } from "@/context/NavigationContext";
import InstrumentLoader from "@/components/InstrumentLoader";
import { message } from "antd";
import AppConst from "../AppConst";
import { PartDto, PartDetailsDto } from "@/types";
import { PART_STATES, PART_CATEGORIES } from "../enums";

const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const AircraftPartDetails = () => {
  const { params, navigateTo } = useNavigation();
  const partId = params?.partId;
  const partListingFromParams = params?.part as (PartDto & Partial<PartDetailsDto>) | undefined;
  const seller = params?.seller;

  const { aircraftParts, getAircraftPartDetails } = useAppContext();
  const { addToCart } = useCart();
  const { isAuthenticated, myAircraftParts } = useAuth();

  const [details, setDetails] = useState<PartDetailsDto | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);

  const isSeller = useMemo(() => {
    const isSellerParam = Number(seller) === 1 || seller === "true" || seller === true;

    const targetId = partId || partListingFromParams?.partId;
    const isInMyParts = myAircraftParts && targetId
      ? myAircraftParts.some((myPart: any) => Number(myPart.partId) === Number(targetId))
      : false;

    return !!(isSellerParam || isInMyParts);
  }, [seller, partId, partListingFromParams, myAircraftParts]);

  const basePart = useMemo(() => {
    if (partListingFromParams) return partListingFromParams;
    if (!partId) return null;
    return aircraftParts?.find(p => Number(p.partId) === Number(partId)) || null;
  }, [aircraftParts, partId, partListingFromParams]);

  useEffect(() => {
    const targetId = Number(partId || basePart?.partId);
    if (!targetId) {
      setIsLoadingDetails(false);
      return;
    }

    let isMounted = true;
    setIsLoadingDetails(true);

    getAircraftPartDetails(targetId)
      .then((data) => {
        if (isMounted && data) {
          setDetails(data);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [partId, basePart?.partId, getAircraftPartDetails]);

  const listing = useMemo(() => {
    if (!basePart) return null;

    const merged = {
      ...basePart,
      ...(details || {})
    };

    const categoryObj = PART_CATEGORIES.find((cat) => cat.code === merged.category);
    const resolvedCategoryLabel = categoryObj ? categoryObj.label : merged.category;

    const stateObj = PART_STATES.find((s) => s.code === merged.state);
    const resolvedStateLabel = stateObj ? stateObj.label : merged.state;

    return {
      ...merged,
      resolvedCategoryLabel,
      resolvedStateLabel
    };
  }, [basePart, details]);

  const images = useMemo(() => {
    if (listing?.mediaUrls && listing.mediaUrls.length > 0) {
      return listing.mediaUrls.filter(url => url && url.trim() !== "");
    }

    if (listing?.media && listing.media.length > 0) {
      return listing.media.map((m: any) => {
        const data = m.mediaData;
        if (!data) return "";
        return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
      }).filter((img: string) => img !== "");
    }

    return [`https://via.placeholder.com/1200x800?text=${encodeURIComponent(listing?.title || "Aircraft Part")}`];
  }, [listing]);

  const isPageLoading = isLoadingDetails && !details;

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <InstrumentLoader visible={true} />
      </div>
    );
  }

  if (!listing) return (
    <div className="text-white text-center py-20 font-sans min-h-screen bg-[#020617]">
      <p className="mb-4 text-slate-500 uppercase tracking-widest font-black">Piesa nu a fost găsită</p>
      <button onClick={() => navigateTo("parts-catalog")} className="text-orange-500 underline font-bold">
        Înapoi la catalog
      </button>
    </div>
  );

  const handleAction = async () => {
    if (isSeller) {
      navigateTo("list-parts", { aircraftPart: listing });
      return;
    }
    if (!isAuthenticated) {
      message.warning("Autentificarea este necesară pentru a adăuga în coș.");
      navigateTo("login");
      return;
    }
    setIsRequesting(true);
    const success = await addToCart(listing, "AIRCRAFT_PART");
    if (success) {
      message.success({
        content: "Produsul a fost adăugat cu succes în coș.",
        duration: AppConst.msgDuration
      });
    }
    setIsRequesting(false);
  };

  const DetailRow = ({ label, value, icon: Icon }: { label: string; value?: string | number | boolean | null; icon?: any }) => {
    if (typeof value !== "boolean" && isInvalidValue(value)) return null;

    return (
      <div className="flex justify-between items-center py-3 border-b border-white/5 group hover:bg-white/[0.02] px-2 transition-colors">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          <span className="text-slate-400 text-[11px] font-bold uppercase tracking-wider">{label}</span>
        </div>
        <span className="text-white font-mono text-sm text-right uppercase">
          {typeof value === "boolean" ? (value ? "DA" : "NU") : value}
        </span>
      </div>
    );
  };

  const hasDescription = !isInvalidValue(listing.description);

  return (
    <div className="container py-8 text-white min-h-screen lg:h-screen flex flex-col lg:overflow-hidden font-sans bg-[#020617]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1 lg:overflow-hidden">
        {/* GALERIE FOTO */}
        <div className="lg:col-span-7 h-auto lg:h-full lg:overflow-y-auto no-scrollbar">
          <div className="flex flex-row lg:flex-col rounded-[2rem] overflow-x-auto lg:overflow-hidden snap-x snap-mandatory no-scrollbar border border-white/10 bg-slate-900 shadow-2xl">
            {images.map((src, idx) => (
              <div
                key={idx}
                className="relative min-w-full lg:min-w-0 snap-center group cursor-zoom-in border-r lg:border-r-0 lg:border-b border-white/5 last:border-0"
                onClick={() => {
                  setPhotoIndex(idx);
                  setIsLightboxOpen(true);
                }}
              >
                <img
                  src={src}
                  alt={`Aircraft component asset ${idx}`}
                  className="w-full h-[350px] md:h-[500px] lg:h-auto object-cover transition-transform duration-500 lg:group-hover:scale-[1.01]"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Image+Unavailable';
                  }}
                />
                <div className="hidden lg:flex absolute inset-0 bg-emerald-600/10 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center">
                  <Maximize2 className="text-white" size={32} />
                </div>
                <div className="lg:hidden absolute bottom-4 right-6 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono border border-white/10 text-white">
                  {idx + 1} / {images.length}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* DETALII TEXT */}
        <div className="lg:col-span-5 h-auto lg:h-full lg:overflow-y-auto no-scrollbar pb-10 lg:pb-20">
          <div className="bg-[#0F172A]/80 backdrop-blur-xl p-8 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-600/10 blur-3xl -z-10" />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-emerald-600 text-[9px] font-black rounded uppercase tracking-tighter">Componentă Aeronautică</span>
                {!isInvalidValue(listing.serialNumber) && (
                  <span className="text-slate-500 font-mono text-[10px] tracking-widest">S/N: {listing.serialNumber}</span>
                )}
              </div>
              <h1 className="text-4xl font-black tracking-tighter mb-1 uppercase italic">
                {listing.manufacturer || "OEM"} <span className="text-slate-500">{listing.title}</span>
              </h1>
              {!isInvalidValue(listing.resolvedCategoryLabel) && (
                <div className="flex items-center gap-2 text-slate-400 text-sm font-bold uppercase tracking-widest">
                  <Layers size={14} className="text-emerald-500" /> {listing.resolvedCategoryLabel}
                </div>
              )}
            </header>

            {/* CARD PRET */}
            <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl mb-8">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Preț Vânzare</p>
                  <span className="text-5xl font-black text-white italic">
                    {listing.grossValue && Number(listing.grossValue) > 0 ? `€${Number(listing.grossValue).toLocaleString()}` : "LA CERERE"}
                  </span>
                </div>
              </div>
              {!(isSeller && listing.verified === false) && (
                <button
                  disabled={isRequesting}
                  className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-75 text-white font-black rounded-xl transition-all flex items-center justify-center gap-3 active:scale-[0.98] uppercase tracking-widest text-sm shadow-lg shadow-emerald-900/20"
                  onClick={handleAction}
                >
                  {isRequesting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Se adaugă...
                    </>
                  ) : isSeller ? (
                    <>
                      <Settings size={18} /> Editează Anunțul
                    </>
                  ) : (
                    "Adaugă în coș"
                  )}
                </button>
              )}
            </div>

            <div className="space-y-10">
              {/* SPECIFICATII */}
              <section>
                <h3 className="text-emerald-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                  <Package size={14} /> Specificații Piese
                </h3>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {!isInvalidValue(listing.resolvedStateLabel) && (
                    <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5">
                      <ShieldCheck size={16} className="text-emerald-500 mb-2" />
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Stare/State</p>
                      <p className="text-xl font-black text-white uppercase">{listing.resolvedStateLabel}</p>
                    </div>
                  )}
                  {!isInvalidValue(listing.quantity) && (
                    <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5">
                      <Boxes size={16} className="text-blue-500 mb-2" />
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Cantitate</p>
                      <p className="text-xl font-black text-white">{listing.quantity}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <DetailRow label="Producător" value={listing.manufacturer} icon={Tag} />
                  <DetailRow label="Categorie" value={listing.resolvedCategoryLabel} icon={Layers} />
                  <DetailRow label="Număr de Serie" value={listing.serialNumber} icon={Hash} />
                </div>
              </section>

              {/* DESCRIERE */}
              {hasDescription && (
                <section className="pt-6 border-t border-slate-800">
                  <h3 className="text-slate-500 text-[10px] uppercase tracking-[0.3em] font-black mb-3 flex items-center gap-2">
                    <Info size={14} /> Descriere Tehnică
                  </h3>
                  <p className="text-slate-400 text-xs leading-relaxed italic whitespace-pre-wrap">
                    {listing.description}
                  </p>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* LIGHTBOX PENTRU IMAGINI */}
      {isLightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300" onClick={() => setIsLightboxOpen(false)}>
          <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center pointer-events-none z-50">
            <span className="text-white font-mono text-sm pointer-events-auto bg-white/5 px-3 py-1 rounded-full border border-white/10">
              {photoIndex + 1} / {images.length}
            </span>
            <button className="p-3 hover:bg-white/10 text-white rounded-full transition-colors pointer-events-auto" onClick={() => setIsLightboxOpen(false)}>
              <X size={32} />
            </button>
          </div>

          <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIndex((p) => (p === 0 ? images.length - 1 : p - 1)); }}
              className="absolute left-4 md:left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronLeft size={40} />
            </button>
            <div
              className="w-full max-w-[80vw] aspect-video rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 bg-slate-900"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[photoIndex]}
                className="w-full h-full object-cover"
                alt="Part Zoom"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Image+Unavailable';
                }}
              />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setPhotoIndex((p) => (p + 1) % images.length); }}
              className="absolute right-4 md:right-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronRight size={40} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AircraftPartDetails;